import { useRef, type ComponentPropsWithoutRef, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronsLeft, ChevronsRight, Eye, EyeOff, GitPullRequest } from 'lucide-react';
import type { BoardFaceList } from '@molt/shared';

import { IconButton, InlineEdit, Menu, type MenuSection } from '@/components';
import { announce } from '@/lib/announce';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  isOptimisticListId,
  useArchiveList,
  useRecolourList,
  useRenameList,
  useSetListPrList,
} from '@/lib/lists';
import { listColourOptions, listColourLabel, listColourStyle } from '@/lib/list-colours';
import type { DragHandle } from '@/lib/list-drag';
import { listUndoSourceKey, type UndoSourceRegistration } from '@/lib/undo';
import type { PowerUpSurfaceSummary } from '@/lib/power-ups';
import { useSetWatchState, watchStateQueryOptions } from '@/lib/watch';

/**
 * The column header — chunk `C2` (`FE-11.1` rename, `FE-11.2` handle,
 * `FE-11.3` archive). Signatures fixed by `docs/design/c2/C2-BRIEF.md` §6.4.
 *
 * Drag handle, `<h2>` title, open card count, actions menu.
 *
 * ⚠ THE DEFECT THIS FILE CAME CLOSEST TO SHIPPING — read before touching the
 * heading. `ListColumn` renders `<section aria-labelledby={headingId}>`
 * pointing at the `<h2>` below, and five e2e spec files plus
 * `board.composition.test.tsx` assert that the column is named exactly the
 * list title.
 *
 * `InlineEdit` has no externally controlled "start editing" prop — the only
 * way it ever leaves `idle` is a click on its OWN trigger button — so for
 * "selecting the list title" to open the editor (`FR-BOARD-010`'s Behaviour)
 * it must be mounted WHOLE, idle button included, inside the heading. That
 * idle button renders `{value}` followed by an unconditional
 * `<span className="sr-only">, edit {label}</span>`, which is plain content
 * rather than an embedded control — so name-from-content folded it in and an
 * editable column began announcing itself as `"Doing, edit Rename list"`. The
 * section inherited it through `aria-labelledby`. Nothing threw; every
 * by-name query below it simply stopped matching.
 *
 * The fix is the explicit `aria-label` on the `<h2>`, which halts the
 * traversal — see the comment at the heading itself for the trade it makes.
 */
export interface ListHeaderProps {
  list: BoardFaceList;
  canEdit: boolean;
  canWatch?: boolean | undefined;
  /** The id the parent `<section>`'s `aria-labelledby` points at. Must always exist. */
  headingId: string;
  /** Opens a composer in the gap to this list's right — `FR-BOARD-009` AC3's keyboard path. */
  onInsertListAfter?: ((listId: string) => void) | undefined;
  onMoveList?: (() => void) | undefined;
  onCopyList?: (() => void) | undefined;
  onOpenListOperations?: (() => void) | undefined;
  recordUndoSource?:
    ((source: UndoSourceRegistration) => Promise<void> | void) | undefined;
  collapsed?: boolean | undefined;
  onCollapseChange?: ((collapsed: boolean) => void) | undefined;
  dragHandle?: DragHandle | undefined;
  powerUpSurfaces?: PowerUpSurfaceSummary | undefined;
}

/*
 * ⚠ `cardCountLabel` USED TO LIVE HERE, and its deletion is what this slice is
 * about. It read `` `${count} ${count === 1 ? 'card' : 'cards'}` `` — a second
 * author of a sentence `boardFace.list.cardCount` had already declared as a
 * `plural` and `ListColumn.tsx:266` had already routed for the collapsed rail.
 * Two authors for one sentence is green in English forever and diverges the
 * moment anything is translated.
 *
 * The `1 cards` history and the `Avatar.tsx` precedent moved with it, into the
 * declaration in `lib/interface-language.tsx` — which is where the next person
 * to consider flattening the plural will actually be standing.
 */

/** Drag glyph — a six-dot grip, the conventional affordance for "this reorders". */
function DragGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" className="size-3.5" aria-hidden="true" focusable="false">
      <circle cx="5" cy="3.5" r="1.1" fill="currentColor" />
      <circle cx="11" cy="3.5" r="1.1" fill="currentColor" />
      <circle cx="5" cy="8" r="1.1" fill="currentColor" />
      <circle cx="11" cy="8" r="1.1" fill="currentColor" />
      <circle cx="5" cy="12.5" r="1.1" fill="currentColor" />
      <circle cx="11" cy="12.5" r="1.1" fill="currentColor" />
    </svg>
  );
}

/** Overflow-menu glyph — three dots, vertical. */
function MoreGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" className="size-3.5" aria-hidden="true" focusable="false">
      <circle cx="8" cy="3" r="1.3" fill="currentColor" />
      <circle cx="8" cy="8" r="1.3" fill="currentColor" />
      <circle cx="8" cy="13" r="1.3" fill="currentColor" />
    </svg>
  );
}

function ListColourSwatch({ colour }: { colour: BoardFaceList['colour'] }): ReactElement {
  return (
    <span
      aria-hidden="true"
      className="size-3.5 rounded-sm border border-border-strong bg-panel"
      style={listColourStyle(colour)}
    />
  );
}

export function ListHeader({
  list,
  canEdit,
  canWatch = false,
  headingId,
  onInsertListAfter,
  onMoveList,
  onCopyList,
  onOpenListOperations,
  recordUndoSource,
  collapsed = false,
  onCollapseChange,
  dragHandle,
  powerUpSurfaces,
}: ListHeaderProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const optimistic = isOptimisticListId(list.id);
  // §5.2's table: an optimistic placeholder gets no menu and no rename — a
  // rename would PATCH an id the server has never seen.
  const editable = canEdit && !optimistic;

  const renameMutation = useRenameList(list.boardId);
  const recolourMutation = useRecolourList(list.boardId);
  const archiveMutation = useArchiveList(list.boardId);
  const prListMutation = useSetListPrList(list.boardId);
  const watchTarget = { targetType: 'list' as const, targetId: list.id };
  const colourOptions = listColourOptions(t);
  const listWatch = useQuery(
    watchStateQueryOptions(watchTarget, canWatch && !optimistic),
  );
  const setWatch = useSetWatchState();

  /*
   * ⚠ THE COUNT IS PASSED TWICE, AND BOTH ARE REQUIRED. The values object fills
   * `{count}` in the text; the THIRD argument is what selects between the
   * `one` and `other` forms. Drop it and `interfaceMessage` takes `other`
   * unconditionally — every list holding exactly one card announces
   * **"1 cards"** again, which is the defect `cardCountLabel` existed to fix
   * and the reason its history now sits on the declaration.
   *
   * ⚠ `list.openCardCount`, NOT `ListColumn.tsx:265`'s `visibleOpenCardCount`.
   * The two call sites feed deliberately different numbers into one sentence —
   * that file subtracts cards a template hides. Routing the sentence must not
   * change either number.
   */
  const cardCountDescription = t(
    'boardFace.list.cardCount',
    { count: list.openCardCount },
    list.openCardCount,
  );

  /*
   * ⏭ THERE IS NO "Rename" MENU ITEM, AND THAT IS A DECISION — parent, at
   * integration. Bringing one back needs two primitive changes; do not
   * reintroduce it without them.
   *
   * Agent C built one and, in doing so, found a real bug worth keeping on the
   * record. `InlineEdit` exposes no imperative "start editing" API — its only
   * entry into `editing` is a click on its own idle `<button>` — so the menu
   * item had to walk the DOM for that button and click it. With
   * `commitOn="both"` that click opened the editor and silently closed it in
   * the same tick: `Menu`'s Radix `DropdownMenu` returns focus to its own
   * trigger when it closes (`Menu.test.tsx` asserts exactly that, as
   * `FR-EXP-022` AC3), the focus move blurred the input `InlineEdit` had just
   * focused, and `'both'`'s `handleBlur` read the untouched draft as "no
   * change, close". No delay fixes it — deferring only moves when the race
   * starts. `commitOn="enter"` below is the fix for the closing half.
   *
   * What could not be fixed from this file is WHERE FOCUS LANDS: Radix still
   * returns it to the menu trigger, so the item opened an editor the member
   * was not in — while `FR-BOARD-010` asks for the field open *with the text
   * selected*. **No acceptance criterion anywhere requires a menu rename**;
   * the requirement's own path is selecting the title, and that path is
   * already keyboard-reachable because `InlineEdit`'s idle trigger is a real
   * `<button>` in the Tab order. A menu entry whose keyboard path misplaces
   * focus is worse than no entry, so it is not shipped.
   *
   * To bring it back, ONE thing is still missing. `Menu` now exposes Radix's
   * `onCloseAutoFocus` — the parent added it at integration for
   * `Add a list to the right`, which has the identical problem and, unlike
   * this, is required (it is `FR-BOARD-009` AC3's only keyboard path). What is
   * still absent is a controlled "start editing" prop on `InlineEdit`, so that
   * a menu rename is a state change rather than a synthetic click on a button
   * found by `querySelector`. That is a design-system change, and a deliberate
   * one — not a fix smuggled in beside a feature.
   */

  function handleArchive() {
    /*
     * No call-site `onError`. This had one until integration, and its removal
     * is the point: `useArchiveList` announces its own rollback, because a
     * call site can forget to and a hook cannot. Three agents independently
     * chose three different homes for failure announcements, and the gap
     * between them shipped a create that announced "added" after being
     * rejected — see `lib/lists.ts`'s `announceRollback`.
     */
    archiveMutation.mutate(
      { listId: list.id },
      {
        onSuccess: () => {
          void recordUndoSource?.({
            boardId: list.boardId,
            scope: { kind: 'board', boardId: list.boardId },
            eventTypes: ['list.archived'],
            sourceKey: listUndoSourceKey(list.id),
            // ⚠ `undo.source.listArchived`, NOT `boardFace.list.archived` —
            // no terminal full stop. `lib/undo.ts`'s `stripTerminal` feeds this
            // into `undo.toast.recorded`'s `{label}` slot mid-sentence, which is
            // why the whole `undo.source.*` family omits one.
            label: t('undo.source.listArchived', { title: list.title }),
          });
        },
      },
    );
    // Optimistic: the list is already gone from `contents.lists` by the time
    // this line runs (brief §5). Announced unconditionally, not gated on the
    // mutation's own promise settling, matching every other write in this
    // chunk's "optimistic write, then announce" shape.
    announce(t('boardFace.list.archived', { title: list.title }));
  }

  /*
   * Set by menu items that move focus somewhere else, read once as the menu
   * closes. A ref rather than state on purpose: it must be readable during
   * Radix's close sequence, and changing it must not re-render.
   */
  const handsOverFocus = useRef(false);

  const powerUpMenuItems =
    powerUpSurfaces === undefined
      ? []
      : [
          ...powerUpSurfaces.listMenuActions.map((entry) => ({
            id: `power-up-action-${entry.id}`,
            label: `${entry.powerUpName}: ${entry.label}`,
            disabled: true,
            onSelect: () => undefined,
          })),
          ...powerUpSurfaces.listMenuSorts.map((entry) => ({
            id: `power-up-sort-${entry.id}`,
            label: `${entry.powerUpName}: ${entry.label}`,
            disabled: true,
            onSelect: () => undefined,
          })),
        ];

  const watchMenuSections: MenuSection[] =
    canWatch && !optimistic
      ? [
          {
            id: 'list-watch',
            items: [
              {
                id: 'watch-list',
                label:
                  listWatch.data?.watching === true
                    ? t('boardFace.list.unwatch')
                    : t('boardFace.list.watch'),
                icon:
                  listWatch.data?.watching === true ? (
                    <EyeOff className="size-3.5" />
                  ) : (
                    <Eye className="size-3.5" />
                  ),
                disabled:
                  listWatch.isLoading ||
                  listWatch.isError ||
                  listWatch.data === undefined ||
                  setWatch.isPending,
                onSelect: () => {
                  if (listWatch.data === undefined || setWatch.isPending) return;
                  setWatch.mutate({
                    targetType: 'list',
                    targetId: list.id,
                    watching: !listWatch.data.watching,
                  });
                },
              },
            ],
          },
        ]
      : [];

  const editMenuSections: MenuSection[] = editable
    ? [
        {
          id: 'list-actions',
          items: [
            ...(onInsertListAfter
              ? [
                  {
                    id: 'insert-after',
                    label: t('boardFace.list.insertAfter'),
                    onSelect: () => {
                      /*
                       * ⚠ This item HANDS OVER — it opens a composer the
                       * member is then supposed to type into — so the menu's
                       * default focus restoration has to be cancelled below,
                       * or Radix puts focus back on this trigger after the
                       * composer has already focused its field. Nothing goes
                       * red; a keyboard member simply types into nothing,
                       * which is precisely what `FR-EXP-022` forbids and what
                       * `FR-BOARD-009` AC3 has no other keyboard path around.
                       */
                      handsOverFocus.current = true;
                      onInsertListAfter(list.id);
                    },
                  },
                ]
              : []),
            ...(onMoveList
              ? [
                  {
                    id: 'move-list',
                    label: t('boardFace.list.move'),
                    onSelect: onMoveList,
                  },
                ]
              : []),
            ...(onCopyList
              ? [
                  {
                    id: 'copy-list',
                    label: t('boardFace.list.copy'),
                    onSelect: () => {
                      handsOverFocus.current = true;
                      onCopyList();
                    },
                  },
                ]
              : []),
            ...(onOpenListOperations
              ? [
                  {
                    id: 'list-operations',
                    label: t('boardFace.list.operations'),
                    onSelect: () => {
                      handsOverFocus.current = true;
                      onOpenListOperations();
                    },
                  },
                ]
              : []),
            {
              id: 'pr-list',
              label: list.isPrList
                ? t('boardFace.list.removePrList')
                : t('boardFace.list.setPrList'),
              icon: <GitPullRequest aria-hidden="true" className="size-3.5" />,
              disabled: prListMutation.isPending,
              onSelect: () => {
                const next = !list.isPrList;
                prListMutation.mutate(
                  { listId: list.id, isPrList: next },
                  {
                    onSuccess: () => {
                      announce(
                        next
                          ? t('boardFace.list.prListSet', { title: list.title })
                          : t('boardFace.list.prListRemoved', { title: list.title }),
                      );
                    },
                  },
                );
              },
            },
            {
              id: 'archive',
              label: t('boardFace.list.archive'),
              // Archiving is reversible (⏭ restore is F3's, `FE-22.2`) —
              // colouring it as destruction is a lie brief §6.4 calls out by
              // name.
              destructive: false,
              onSelect: handleArchive,
            },
          ],
        },
        ...(powerUpMenuItems.length > 0
          ? [
              {
                id: 'list-power-up-actions',
                heading: t('boardFace.list.powerUpActions', { list: list.title }),
                items: powerUpMenuItems,
              },
            ]
          : []),
        {
          id: 'list-colour',
          heading: t('boardFace.list.colours', { list: list.title }),
          selection: 'radio',
          value: list.colour,
          onValueChange: (colour) => {
            const nextColour = colourOptions.find(
              (option) => option.value === colour,
            )?.value;
            if (nextColour === undefined || nextColour === list.colour) return;
            recolourMutation.mutate(
              { listId: list.id, colour: nextColour },
              {
                onSuccess: () => {
                  announce(
                    nextColour === 'none'
                      ? t('boardFace.list.colourCleared', { title: list.title })
                      : t('boardFace.list.recoloured', {
                          title: list.title,
                          colour: listColourLabel(t, nextColour),
                        }),
                  );
                },
              },
            );
          },
          items: colourOptions.map((option) => ({
            id: option.value,
            label: option.label,
            icon: <ListColourSwatch colour={option.value} />,
            disabled: recolourMutation.isPending,
            onSelect: () => undefined,
          })),
        },
      ]
    : [];
  const menuSections: MenuSection[] = [...watchMenuSections, ...editMenuSections];

  // `dragHandle.attributes`/`.listeners` carry dnd-kit's own types
  // (`DraggableAttributes`, `DraggableSyntheticListeners`) and spread straight
  // onto the elements below. They were `Record<string, unknown>` when this file
  // was written, which forced a cast here and a spread in
  // `SortableListColumn`; the parent made the shared type honest at
  // integration, and both casts came out with it.
  //
  // `attributes` (role, tabIndex, aria-describedby, aria-roledescription) goes
  // on the `IconButton` alone — brief §7.3, "that is the element the keyboard
  // sensor focuses". `listeners` (`onPointerDown`/`onKeyDown`) goes on the
  // header `<div>` ONLY, not duplicated onto the button: both a pointerdown
  // that starts on the button and a keydown while the button is focused
  // bubble up to the div (neither event's propagation is stopped anywhere in
  // this tree), so one listener there already covers "drag by the handle" —
  // dnd-kit's keyboard path still resolves to the button specifically because
  // `setActivatorNodeRef` marks it as the activator node and dnd-kit compares
  // against the bubbled event's original `target`, not against whichever node
  // the listener happens to be attached to. Attaching `listeners` a second
  // time directly on the button as well would fire the same handler twice for
  // a single interaction that starts there.
  const dragButtonProps: ComponentPropsWithoutRef<'button'> = {
    ...dragHandle?.attributes,
  };
  const dragContainerProps: ComponentPropsWithoutRef<'div'> = {
    ...dragHandle?.listeners,
  };

  return (
    <div
      className="flex shrink-0 items-center gap-2 px-1 pb-2"
      // `FR-BOARD-011`'s prose: a list is dragged "by its header", not only by
      // the handle. `useMoltSensors()`'s `PointerSensor` keeps a plain click
      // on the title from registering as a drag
      // (`activationConstraint: { distance: 6 }`, `src/lib/dnd.ts`); this
      // file must not remove or lower it.
      {...dragContainerProps}
    >
      {dragHandle && (
        <IconButton
          ref={dragHandle.setActivatorNodeRef}
          label={t('boardFace.list.reorder', { list: list.title })}
          size="sm"
          /*
            ⚠ `touch-none` is required, and it belongs HERE and nowhere else.
            dnd-kit's `PointerSensor` cannot begin a touch drag unless the
            activator suppresses the browser's own touch panning — added by the
            parent at integration on agent E's report (`FR-EXP-023`'s
            territory; no C2 test or spec covers touch). It must NOT go on the
            header container or the `<li>`: that would kill touch scrolling of
            the strip and of the card stack, trading one broken interaction for
            two.
          */
          className="touch-none"
          {...dragButtonProps}
        >
          <DragGlyph />
        </IconButton>
      )}

      {/*
        `title` (not `Tooltip`) for the long-title hover case (`FR-BOARD-010`
        edge case). `Tooltip` needs a single ref-forwarding child
        (`Tooltip.tsx`'s own contract) and `InlineEdit` is a plain function
        component with no `forwardRef` — wrapping it would silently drop
        positioning. The native attribute needs no ref, sits on the element
        that already truncates, and works identically whether the title is
        plain text or `InlineEdit`'s own rendering.
      */}
      {/*
        ⚠ `aria-label`, added by the parent at integration, and it is the fix
        for the defect this chunk came closest to shipping.

        C1's DOM contract names the column `<section aria-labelledby>` → this
        `<h2>` → the list title, and five e2e spec files plus the composition
        test assert exactly that string. `InlineEdit` has no externally
        controlled "start editing" API, so it must be mounted WHOLE — idle
        button included — inside the heading for a title click to open it; and
        its idle button unconditionally appends `<span class="sr-only">, edit
        {label}</span>`. That span is plain content, not an embedded control,
        so name-from-content folds it in: an editable column announced itself
        as **"Doing, edit Rename list"**, and the section inherited it through
        `aria-labelledby`. Nothing throws, nothing renders wrong, and every
        by-name query in the chunk below it stops matching.

        An explicit label on the heading halts the content traversal, so the
        heading and the section are both named exactly the list title whether
        or not the viewer can edit — while the button keeps the "edit" hint as
        its OWN name, which is where it was always useful. The trade, stated:
        while a rename is open the name is the committed title rather than the
        text being typed. That is the more honest of the two — the list is
        still called that until Enter commits.
      */}
      <h2
        id={headingId}
        aria-label={list.title}
        title={list.title}
        className="min-w-0 flex-1 truncate font-display text-sm font-semibold"
      >
        {editable ? (
          <InlineEdit
            value={list.title}
            label={t('boardFace.list.rename')}
            onCommit={(next) =>
              // `mutateAsync` resolves with the server's `List`; `InlineEdit`
              // only wants to know when the write settles, so the value is
              // discarded to fit `Promise<void>`.
              renameMutation
                .mutateAsync({ listId: list.id, title: next })
                .then(() => undefined)
            }
            // `FR-BOARD-010`'s Behaviour is narrower than its card-title
            // sibling (`FR-CARD-002`, "saves on blur or Enter"): it enumerates
            // exactly two exits — "Enter commits and returns to display mode;
            // Escape cancels" — and blur is not one of them. `'enter'` is that
            // reading exactly, and it is not merely the closer reading: `'both'`
            // is actively broken for the "Rename" menu item below. Measured,
            // not guessed — `commitOn="both"` made `openRenameFromMenu` open
            // the editor and then silently close it in the same tick, because
            // `Menu`'s Radix `DropdownMenu` returns focus to its own trigger on
            // close (`Menu.test.tsx` asserts exactly this), that focus move
            // blurs the rename `<input>` `InlineEdit`'s own effect had just
            // focused, and `handleBlur` (`'both'`) read the untouched draft as
            // "no change, close". `'enter'`'s `handleBlur` no-ops
            // unconditionally (`InlineEdit.tsx`: `if (commitOn === 'enter')
            // return;`), so that stray blur no longer commits anything.
            commitOn="enter"
            emptyPolicy="reject-and-restore"
            className="truncate"
            controlClassName="truncate"
          />
        ) : (
          list.title
        )}
      </h2>

      {/*
        Unchanged from `ListColumn.tsx` — moved here with the rest of the
        header row. See that file's former comment (now here) for why the
        `aria-label` is backed by an `sr-only` fallback rather than relying on
        `aria-label` alone on a bare `<span>`.
      */}
      <span aria-label={cardCountDescription} className="shrink-0 text-sm">
        <span aria-hidden="true">{list.openCardCount}</span>
        <span className="sr-only">{cardCountDescription}</span>
      </span>

      {list.isPrList ? (
        <span
          role="img"
          data-pr-list-icon
          aria-label={t('boardFace.list.prListIcon', { list: list.title })}
          title={t('boardFace.list.prListIcon', { list: list.title })}
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-card text-ink-muted"
        >
          <GitPullRequest aria-hidden="true" className="size-3.5" />
        </span>
      ) : null}

      {canWatch && !optimistic && listWatch.data?.watching === true ? (
        <span
          aria-label={t('boardFace.list.watching', { list: list.title })}
          title={t('boardFace.list.watching', { list: list.title })}
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-card text-ink-muted"
        >
          <Eye aria-hidden="true" className="size-3.5" />
        </span>
      ) : null}

      {onCollapseChange !== undefined && !optimistic ? (
        <IconButton
          label={
            collapsed
              ? t('boardFace.list.expand', { list: list.title })
              : t('boardFace.list.collapse', { list: list.title })
          }
          size="sm"
          className="hidden md:inline-flex"
          aria-pressed={collapsed}
          onClick={() => onCollapseChange(!collapsed)}
        >
          {collapsed ? (
            <ChevronsRight aria-hidden="true" className="size-3.5" />
          ) : (
            <ChevronsLeft aria-hidden="true" className="size-3.5" />
          )}
        </IconButton>
      ) : null}

      {menuSections.length > 0 && (
        <Menu
          trigger={
            <IconButton
              label={t('boardFace.list.actions', { list: list.title })}
              size="sm"
            >
              <MoreGlyph />
            </IconButton>
          }
          sections={menuSections}
          onCloseAutoFocus={(event) => {
            // Default (focus back to the trigger) for every item that acts in
            // place. Cancelled only for entries that deliberately move focus
            // into a composer or dialog.
            if (!handsOverFocus.current) return;
            handsOverFocus.current = false;
            event.preventDefault();
          }}
          align="end"
        />
      )}
    </div>
  );
}
